import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock3, Settings, Wallet, X } from "lucide-react";

type Servico = {
  id: number;
  nome: string;
  tipo_precificacao?: string;
  preco_base?: string | number | null;
  preco_banho?: string | number | null;
  preco_tosa?: string | number | null;
  preco_banho_tosa?: string | number | null;
  status?: string;
  horario_inicio?: string;
  horario_fim?: string;
  intervalo_minutos?: number;
  duracao_minutos?: number;
  max_pets?: number | null;
  valor_liquido_desejado?: string | number | null;
  descricao?: string | null;
};

type Props = {
  open: boolean;
  servico: Servico | null;
  onClose: () => void;
  onEditar?: (id: number) => void;
  onAgenda?: (id: number) => void;
};

const TAXA_TOTAL = 0.25;
const PERCENTUAL_LIQUIDO = 1 - TAXA_TOTAL;

function normalizarTexto(valor?: string | null) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function toNumber(valor?: string | number | null) {
  if (valor === null || valor === undefined || valor === "") return 0;
  const n = typeof valor === "string" ? Number(valor.replace(/\./g, "").replace(",", ".")) : valor;
  return Number.isFinite(n) ? n : 0;
}

function moedaParaCampo(valor?: string | number | null) {
  const numero = toNumber(valor);
  return numero > 0 ? numero.toFixed(2).replace(".", ",") : "";
}

function formatarPreco(valor?: string | number | null) {
  if (valor === null || valor === undefined || valor === "") return "Não informado";
  const numero = toNumber(valor);
  return numero.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function calcularCobrado(valorLiquido: number) {
  return valorLiquido > 0 ? valorLiquido / PERCENTUAL_LIQUIDO : 0;
}

function calcularLiquido(valorCobrado: number) {
  return valorCobrado > 0 ? valorCobrado * PERCENTUAL_LIQUIDO : 0;
}

function calcularTaxa(valorCobrado: number) {
  return valorCobrado > 0 ? valorCobrado * TAXA_TOTAL : 0;
}

function formatarTipo(tipo?: string) {
  const t = normalizarTexto(tipo);
  if (t === "horaria" || t === "hora") return "Cobrança por hora";
  if (t === "diaria") return "Cobrança por diária";
  if (t === "creche") return "Cobrança por creche";
  return tipo || "Não informado";
}

function formatarStatus(status?: string) {
  const s = normalizarTexto(status || "ativo");
  if (s === "ativo") return "Ativo";
  if (s === "inativo") return "Inativo";
  if (s === "disponivel") return "Disponível";
  if (s === "indisponivel") return "Indisponível";
  return status || "Ativo";
}

function ehBanhoTosa(servico: Servico) {
  const nome = normalizarTexto(servico.nome);
  return nome.includes("banho") && nome.includes("tosa");
}

function chaveMaxPets(servico: Servico) {
  const nome = normalizarTexto(servico.nome);
  if (nome.includes("hospedagem")) return "max_pets_hospedagem";
  if (nome.includes("creche")) return "max_pets_creche";
  if (nome.includes("passeio")) return "max_pets_passeio";
  return null;
}

export default function ServicoDetalhesModal({ open, servico, onClose, onEditar, onAgenda }: Props) {
  const [editandoPreco, setEditandoPreco] = useState(false);
  const [salvandoPreco, setSalvandoPreco] = useState(false);
  const [maxPetsPerfil, setMaxPetsPerfil] = useState<number | null>(null);
  const [liquidoPadrao, setLiquidoPadrao] = useState("");
  const [liquidoBanho, setLiquidoBanho] = useState("");
  const [liquidoTosa, setLiquidoTosa] = useState("");
  const [liquidoBanhoTosa, setLiquidoBanhoTosa] = useState("");

  useEffect(() => {
    if (!open || !servico) return;

    setEditandoPreco(false);
    setLiquidoPadrao(moedaParaCampo(servico.valor_liquido_desejado ?? calcularLiquido(toNumber(servico.preco_base))));
    setLiquidoBanho(moedaParaCampo(calcularLiquido(toNumber(servico.preco_banho))));
    setLiquidoTosa(moedaParaCampo(calcularLiquido(toNumber(servico.preco_tosa))));
    setLiquidoBanhoTosa(moedaParaCampo(calcularLiquido(toNumber(servico.preco_banho_tosa))));

    const campo = chaveMaxPets(servico);
    setMaxPetsPerfil(servico.max_pets ?? null);

    if (!campo) return;

    fetch("/anfitriao/perfil-dados", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const valor = data?.host_request?.[campo];
        if (valor !== null && valor !== undefined && valor !== "") setMaxPetsPerfil(Number(valor));
      })
      .catch(() => undefined);
  }, [open, servico]);

  const previews = useMemo(() => {
    const base = toNumber(liquidoPadrao);
    const banho = toNumber(liquidoBanho);
    const tosa = toNumber(liquidoTosa);
    const banhoTosa = toNumber(liquidoBanhoTosa);

    return {
      base: calcularCobrado(base),
      taxaBase: calcularTaxa(calcularCobrado(base)),
      banho: calcularCobrado(banho),
      tosa: calcularCobrado(tosa),
      banhoTosa: calcularCobrado(banhoTosa),
    };
  }, [liquidoPadrao, liquidoBanho, liquidoTosa, liquidoBanhoTosa]);

  if (!open || !servico) return null;

  const statusFormatado = formatarStatus(servico.status);
  const statusAtivo = ["ativo", "disponivel", ""].includes(normalizarTexto(servico.status));
  const liquidoAtual = servico.valor_liquido_desejado ?? calcularLiquido(toNumber(servico.preco_base));
  const temBanhoTosa = ehBanhoTosa(servico);

  async function salvarPrecoPadrao() {
    const liquido = toNumber(liquidoPadrao);
    if (liquido <= 0) {
      alert("Informe o valor líquido padrão.");
      return;
    }

    const payload: Record<string, number | string | null> = {
      valor_liquido_desejado: liquido,
      preco_base: Number(previews.base.toFixed(2)),
      modo_taxa: "assumir",
    };

    if (temBanhoTosa) {
      if (toNumber(liquidoBanho) <= 0 || toNumber(liquidoTosa) <= 0 || toNumber(liquidoBanhoTosa) <= 0) {
        alert("Informe os valores líquidos de Banho, Tosa e Banho e Tosa.");
        return;
      }

      payload.preco_banho = Number(previews.banho.toFixed(2));
      payload.preco_tosa = Number(previews.tosa.toFixed(2));
      payload.preco_banho_tosa = Number(previews.banhoTosa.toFixed(2));
    }

    try {
      setSalvandoPreco(true);
      const resp = await fetch(`/anfitriao/atualizar-preco-base/${servico.id}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const erro = await resp.json().catch(() => ({}));
        throw new Error(erro.message || "Erro ao atualizar preço padrão.");
      }

      window.location.reload();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao atualizar preço padrão.");
    } finally {
      setSalvandoPreco(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-white/40 bg-white shadow-2xl">
        <div className="border-b border-gray-100 bg-gradient-to-r from-white via-[#f8fafc] to-[#f3f4f6] px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusAtivo ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-700"}`}>
                  {statusFormatado}
                </span>
                <span className="inline-flex rounded-full bg-[#eef2ff] px-3 py-1 text-xs font-semibold text-[#4338ca]">
                  {formatarTipo(servico.tipo_precificacao)}
                </span>
              </div>
              <h2 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">{servico.nome || "Serviço"}</h2>
              <p className="mt-1 text-sm text-gray-500">Gerencie as configurações do serviço e ajuste agenda, bloqueios e preços personalizados.</p>
            </div>
            <button onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-gray-700" aria-label="Fechar modal">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <section className="rounded-3xl border border-black/5 bg-[#fafafa] p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-gray-600" /><h3 className="text-sm font-semibold text-gray-900">Resumo do serviço</h3></div>
              <div className="space-y-3 text-sm text-gray-700">
                <Info label="Preço padrão líquido" value={formatarPreco(liquidoAtual)} strong />
                <Info label="Valor cobrado no anúncio" value={formatarPreco(servico.preco_base)} strong />
                {temBanhoTosa && <div><p className="text-xs font-medium uppercase tracking-wide text-gray-400">Valores cobrados por modalidade</p><div className="mt-1 grid gap-1 text-sm"><span>Banho: <strong>{formatarPreco(servico.preco_banho)}</strong></span><span>Tosa: <strong>{formatarPreco(servico.preco_tosa)}</strong></span><span>Banho e Tosa: <strong>{formatarPreco(servico.preco_banho_tosa)}</strong></span></div></div>}
                <Info label="Tipo de cobrança" value={formatarTipo(servico.tipo_precificacao)} />
                <Info label="Status atual" value={statusFormatado} />
              </div>
            </section>

            <section className="rounded-3xl border border-black/5 bg-[#fafafa] p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-gray-600" /><h3 className="text-sm font-semibold text-gray-900">Agenda padrão</h3></div>
              <div className="space-y-3 text-sm text-gray-700">
                <Info label="Disponibilidade" value="100%" strong />
                <Info label="Atendimento padrão" value="Domingo a domingo" />
                <Info label="Horário padrão" value={`${servico.horario_inicio || "05:00"} às ${servico.horario_fim || "23:00"}`} />
                <Info label="Observação" value="Horários, bloqueios e preços personalizados são ajustados na agenda." />
              </div>
            </section>

            <section className="rounded-3xl border border-black/5 bg-[#fafafa] p-4 shadow-sm md:col-span-2">
              <div className="mb-3 flex items-center gap-2"><Clock3 className="h-4 w-4 text-gray-600" /><h3 className="text-sm font-semibold text-gray-900">Configuração operacional</h3></div>
              <div className="grid grid-cols-1 gap-4 text-sm text-gray-700 sm:grid-cols-3">
                <Info label="Duração do atendimento" value={`${servico.duracao_minutos ?? 60} minuto(s)`} />
                <Info label="Intervalo automático" value={`${servico.intervalo_minutos ?? 30} minuto(s)`} />
                <Info label="Máximo de pets" value={maxPetsPerfil === null || maxPetsPerfil === undefined ? "Não se aplica" : `${maxPetsPerfil} pet(s)`} />
              </div>
            </section>
          </div>

          <section className="mt-4 rounded-3xl border border-black/5 bg-[#fafafa] p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-gray-600" /><h3 className="text-sm font-semibold text-gray-900">Demonstrativo interno</h3></div>
            <div className="grid grid-cols-1 gap-4 text-sm text-gray-700 sm:grid-cols-3">
              <Info label="Valor cobrado do tutor" value={formatarPreco(servico.preco_base)} strong />
              <Info label="Taxa total 25%" value={formatarPreco(calcularTaxa(toNumber(servico.preco_base)))} strong />
              <Info label="Líquido do anfitrião" value={formatarPreco(liquidoAtual)} strong />
            </div>
            <p className="mt-3 text-xs text-gray-500">O tutor vê apenas o valor cobrado. A composição da taxa é informação interna.</p>
          </section>

          {servico.descricao ? <section className="mt-4 rounded-3xl border border-black/5 bg-[#fafafa] p-4 shadow-sm"><div className="mb-3 flex items-center gap-2"><Settings className="h-4 w-4 text-gray-600" /><h3 className="text-sm font-semibold text-gray-900">Descrição</h3></div><p className="text-sm leading-6 text-gray-700">{servico.descricao}</p></section> : null}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button onClick={() => onEditar?.(servico.id)} className="inline-flex items-center justify-center rounded-2xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-black">Editar serviço</button>
            <button onClick={() => setEditandoPreco((atual) => !atual)} className="inline-flex items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100">Alterar preço padrão</button>
            <button onClick={() => onAgenda?.(servico.id)} className="inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">Abrir agenda</button>
          </div>

          {editandoPreco && <section className="mt-4 rounded-3xl border border-emerald-100 bg-emerald-50/60 p-4"><div className="mb-4"><h3 className="text-sm font-semibold text-gray-900">Alterar preço padrão</h3><p className="mt-1 text-xs text-gray-600">Informe o valor líquido. O sistema calcula automaticamente o valor cobrado considerando a taxa fixa de 25%.</p></div><div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><Campo label="Valor líquido padrão" value={liquidoPadrao} onChange={setLiquidoPadrao} placeholder="Ex: 100,00" /><div className="rounded-2xl border border-emerald-100 bg-white px-4 py-3"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Valor cobrado no anúncio</p><p className="mt-1 text-base font-bold text-gray-900">{formatarPreco(previews.base)}</p></div><div className="rounded-2xl border border-emerald-100 bg-white px-4 py-3"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Taxa total 25%</p><p className="mt-1 text-base font-bold text-gray-900">{formatarPreco(previews.taxaBase)}</p></div>{temBanhoTosa && <><Campo label="Banho líquido" value={liquidoBanho} onChange={setLiquidoBanho} placeholder="Ex: 80,00" /><Campo label="Tosa líquido" value={liquidoTosa} onChange={setLiquidoTosa} placeholder="Ex: 90,00" /><Campo label="Banho e Tosa líquido" value={liquidoBanhoTosa} onChange={setLiquidoBanhoTosa} placeholder="Ex: 150,00" /></>}</div><div className="mt-4 flex flex-col gap-2 sm:flex-row"><button type="button" onClick={salvarPrecoPadrao} disabled={salvandoPreco} className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60">{salvandoPreco ? "Salvando..." : "Salvar preço padrão"}</button><button type="button" onClick={() => setEditandoPreco(false)} className="inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">Cancelar</button></div></section>}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return <div><p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p><p className={strong ? "mt-1 text-base font-semibold text-gray-900" : "mt-1"}>{value}</p></div>;
}

function Campo({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return <div><label className="mb-1 block text-xs font-semibold uppercase text-gray-500">{label}</label><input value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" placeholder={placeholder} /></div>;
}
